import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import BluetoothScaleAdapter, { WeightData, BluetoothScaleDevice } from '../pages/pda/components/BluetoothScaleAdapter';
import i18n from '../i18n';

interface BluetoothScaleState {
  // 蓝牙适配器实例
  adapter: BluetoothScaleAdapter | null;
  // 连接状态
  isConnected: boolean;
  // 设备名称
  deviceName: string;
  // 连接状态文本
  connectionStatus: string;
  // 当前重量
  currentWeight: string;
  // 重量单位
  weightUnit: string;
  // 当前重量数据（包含数值、单位和原始数据）
  currentWeightData: {weight: number, unit: string, rawData: string} | null;
  // 是否正在扫描设备
  isScanning: boolean;
  // 已发现的设备列表
  discoveredDevices: BluetoothScaleDevice[];
  // 是否显示设备列表
  showDeviceList: boolean;
  // 是否同时称重
  weighSimultaneously: boolean;

  // 操作方法
  initAdapter: () => void;
  scanDevices: () => Promise<void>;
  connectDevice: (address?: string) => Promise<void>;
  disconnect: () => Promise<void>;
  setIsScanning: (isScanning: boolean) => void;
  setShowDeviceList: (show: boolean) => void;
  setDiscoveredDevices: (devices: BluetoothScaleDevice[]) => void;
  updateConnectionStatus: (status: string) => void;
  updateWeightData: (data: WeightData) => void;
  clearWeightData: () => void;
  cleanup: () => Promise<void>;
  setWeighSimultaneously: (enabled: boolean) => void;
}

export const useBluetoothScaleStore = create<BluetoothScaleState>()(
  persist(
    (set, get) => ({
  adapter: null,
  isConnected: false,
  deviceName: '',
  connectionStatus: '',
  currentWeight: '0',
  weightUnit: '',
  currentWeightData: null,
  isScanning: false,
  discoveredDevices: [],
  showDeviceList: false,
  weighSimultaneously: false,

  // 初始化蓝牙适配器
  initAdapter: () => {
    const state = get();
    if (state.adapter) {
      // 已经初始化过，不重复初始化
      return;
    }

    const adapter = new BluetoothScaleAdapter();

    // 设置重量数据回调
    adapter.onWeightChange((weightData: WeightData) => {
      console.log('[BluetoothStore] 接收到重量数据:', weightData);

      // 检测是否为断开连接时的清零信号（重量为0且单位为空）
      if (weightData.weight === 0 && weightData.unit === '') {
        console.log('[BluetoothStore] 检测到断开连接，清空所有状态');
        set({
          isConnected: false,
          deviceName: '',
          connectionStatus: '',
          currentWeight: '0',
          weightUnit: '',
          currentWeightData: null,
          weighSimultaneously: false, // 蓝牙断开时重置同时称重状态
        });
        return;
      }

      // 更新重量数据
      // 判断是否为负数：检查 rawData 的第一个字符
      const isNegative = weightData.rawData && weightData.rawData.charAt(0) === '-';
      const displayWeight = isNegative ? `-${Math.abs(weightData.weight)}` : String(weightData.weight);

      set({
        currentWeight: displayWeight,
        weightUnit: weightData.unit,
        currentWeightData: {
          weight: weightData.weight,
          unit: weightData.unit,
          rawData: weightData.rawData,
        },
      });
    });

    // 设置错误回调
    adapter.onError((errorData) => {
      console.error('[BluetoothStore] 蓝牙错误:', errorData);
    });

    set({ adapter });
  },

  // 扫描蓝牙设备
  scanDevices: async () => {
    const state = get();
    if (!state.adapter) {
      console.error('[BluetoothStore] 适配器未初始化');
      return;
    }

    set({ isScanning: true, discoveredDevices: [] });

    try {
      // 初始化蓝牙
      const initSuccess = await state.adapter.initBluetooth();
      if (!initSuccess) {
        set({ isScanning: false });
        throw new Error(i18n.t('pda.pages.bluetoothScale.initFailed') as string);
      }

      // 开始扫描
      await state.adapter.requestDevice();

      // 等待扫描完成（使用 Promise 包装 setTimeout）
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          const currentState = get();
          if (currentState.adapter) {
            const devices = currentState.adapter.getDiscoveredDevices();
            console.log('[BluetoothStore] 发现的设备:', devices);
            set({
              discoveredDevices: devices,
              isScanning: false,
              showDeviceList: devices.length > 0,
            });
          }
          resolve();
        }, 10000);
      });
    } catch (error) {
      console.error('[BluetoothStore] 搜索设备失败:', error);
      set({ isScanning: false });
      throw error;
    }
  },

  // 连接到指定设备
  connectDevice: async (address?: string) => {
    const state = get();
    if (!state.adapter) {
      console.error('[BluetoothStore] 适配器未初始化');
      return;
    }

    set({ showDeviceList: false });

    try {
      const result = await state.adapter.connectDevice(address);

      if (result.success) {
        set({
          isConnected: true,
          deviceName: result.deviceInfo?.deviceName || '未知设备',
          connectionStatus: '已连接',
        });
      } else {
        set({
          connectionStatus: '连接失败',
        });
        throw new Error(result.error || '连接失败');
      }
    } catch (error) {
      console.error('[BluetoothStore] 连接失败:', error);
      set({ connectionStatus: '连接失败' });
      throw error;
    }
  },

  // 断开连接
  disconnect: async () => {
    const state = get();
    if (!state.adapter) {
      console.error('[BluetoothStore] 适配器未初始化');
      return;
    }

    await state.adapter.disconnect();
    set({
      isConnected: false,
      deviceName: '',
      connectionStatus: '',
      currentWeight: '0',
      weightUnit: '',
      currentWeightData: null,
      weighSimultaneously: false, // 断开连接时重置同时称重状态
    });
  },

  setIsScanning: (isScanning: boolean) => set({ isScanning }),
  setShowDeviceList: (show: boolean) => set({ showDeviceList: show }),
  setDiscoveredDevices: (devices: BluetoothScaleDevice[]) => set({ discoveredDevices: devices }),
  updateConnectionStatus: (status: string) => set({ connectionStatus: status }),

  updateWeightData: (data: WeightData) => {
    // 判断是否为负数：检查 rawData 的第一个字符
    const isNegative = data.rawData && data.rawData.charAt(0) === '-';
    const displayWeight = isNegative ? `-${Math.abs(data.weight)}` : String(data.weight);

    set({
      currentWeight: displayWeight,
      weightUnit: data.unit,
      currentWeightData: {
        weight: data.weight,
        unit: data.unit,
        rawData: data.rawData,
      },
    });
  },

  clearWeightData: () => {
    set({
      currentWeight: '0',
      weightUnit: '',
      currentWeightData: null,
    });
  },

  // 清理蓝牙连接（用于登出等场景）
  cleanup: async () => {
    const state = get();
    console.log('[BluetoothStore] 开始清理蓝牙连接');
    
    // 如果有连接，先断开
    if (state.isConnected && state.adapter) {
      try {
        await state.adapter.disconnect();
        console.log('[BluetoothStore] 蓝牙连接已断开');
      } catch (error) {
        console.error('[BluetoothStore] 断开蓝牙时出错:', error);
      }
    }

    // 关闭蓝牙适配器
    if (state.adapter) {
      try {
        await state.adapter.closeBluetooth();
        console.log('[BluetoothStore] 蓝牙适配器已关闭');
      } catch (error) {
        console.error('[BluetoothStore] 关闭蓝牙适配器时出错:', error);
      }
    }

    // 清除 localStorage 中的持久化数据
    try {
      localStorage.removeItem('bluetooth-scale-storage');
      console.log('[BluetoothStore] localStorage 已清除');
    } catch (error) {
      console.error('[BluetoothStore] 清除 localStorage 时出错:', error);
    }

    // 重置所有状态
    set({
      adapter: null,
      isConnected: false,
      deviceName: '',
      connectionStatus: '',
      currentWeight: '0',
      weightUnit: '',
      currentWeightData: null,
      isScanning: false,
      discoveredDevices: [],
      showDeviceList: false,
      weighSimultaneously: false,
    });
  },

  setWeighSimultaneously: (enabled: boolean) => {
    // 如果关闭同时称重且当前已连接蓝牙，则主动断开连接
    if (!enabled) {
      const state = get();
      if (state.isConnected && state.adapter) {
        console.log('[BluetoothStore] 关闭同时称重，主动断开蓝牙连接');
        // 调用Store的disconnect方法，确保状态正确更新
        state.disconnect().then(() => {
          console.log('[BluetoothStore] 蓝牙连接已断开');
        }).catch(error => {
          console.error('[BluetoothStore] 断开蓝牙失败:', error);
        });
      }
    }
    set({ weighSimultaneously: enabled });
  },
}),
    {
      name: 'bluetooth-scale-storage', // localStorage 的 key
      partialize: (state) => {
        // 只持久化连接状态和设备信息，不持久化 adapter 实例和临时状态
        return {
          isConnected: state.isConnected,
          deviceName: state.deviceName,
          // connectionStatus 不持久化，因为它是翻译文本，应该根据当前语言动态生成
          currentWeight: state.currentWeight,
          weightUnit: state.weightUnit,
          currentWeightData: state.currentWeightData,
          weighSimultaneously: state.weighSimultaneously,
          // 不持久化：adapter, connectionStatus, isScanning, discoveredDevices, showDeviceList
        };
      },
    }
  )
);

